import axios from "axios";
import { ApiUrlConstant } from "@constants/apiUrls";

export async function getLoginDto(accessToken: string) {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "Content-Type": "application/json",
        "product-id": "1000",
        "tenant-id": "1",
        platform: "web",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const response = await api.get(`/api/v1/user/agents/dto`);
    return response?.data ?? Promise.reject("Failed to fetch login DTO");
  } catch (error: any) {
    console.error("Error fetching login DTO:", error);
    throw new Error(error?.message || String(error));
  }
}

interface LoginRequest {
  phone: string;
  password?: string;
  otp?: string;
}

interface LoginResponse {
  token?: string;
  message?: string;
  [key: string]: any;
}

export async function login(
  obj: LoginRequest,
  countryCode: string
): Promise<LoginResponse> {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "tenant-id": "1",
        "product-id": "1000",
        platform: "web",
      },
    });

    const body: any = {
      isd_code: countryCode,
      phone: obj.phone.toString(),
      ...(obj.password ? { password: obj.password } : { userOtp: obj.otp }),
    };

    const response = await api.post(`/api/v1/user/agents/login`, body);
    return response.data;
  } catch (error: any) {
    console.error("Login failed:", error);
    throw error?.response?.data || error?.message;
  }
}

export async function verifyPhoneNumber(phoneData: {
  dialCode: string;
  number: string;
}) {
  try {
    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: {
        "Content-Type": "application/json",
        "tenant-id": "1",
        "product-id": "1000",
        platform: "web",
      },
    });

    const response = await api.post(`/api/v1/user/agents/verify`, {
      isd_code: phoneData.dialCode,
      phone: phoneData.number.toString(),
    });

    return response?.data ?? Promise.reject("Failed to verify phone number");
  } catch (error: any) {
    console.error("Error verifying phone number:", error);
    throw new Error(error?.message || String(error));
  }
}

export async function generateOtp(obj: any) {
  try {
    const url = `/api/v3/generate-otp`;
    const api = axios.create({
      baseURL: ApiUrlConstant.commServiceUrl,
      headers: {
        "Content-Type": "application/json",
      },
    });
    const body: any = {
      isd_code: obj?.dialCode?.toString(),
      phone: obj?.number?.toString(),
      tenant_id: 1,
      product_id: 1000,
      ...(obj.firstName && { firstName: obj.firstName }),
      ...(obj.lastName && { lastName: obj.lastName }),
      ...(obj.gradeId && { gradeId: obj.gradeId }),
      ...(obj.admissionNumber && { admissionNumber: obj.admissionNumber }),
      ...(obj?.captcha_token && { captcha_token: obj.captcha_token }),
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (obj?.register) {
      headers["X-Page"] = "REGISTER_GENERATE";
      headers["X-Platform"] = "web";
      headers["X-Product"] = "1";
    }

    const response = await api.post(url, body, { headers });

    return response?.data ?? Promise.reject("Failed to generate OTP");
  } catch (error: any) {
    console.error("Error generating OTP:", error);
    throw new Error(error?.message || String(error));
  }
}

export async function validateOtp(inp: {
  countryCode: string;
  phone: string;
  otp: string;
  register?: boolean;
  page?: string;
}) {
  try {
    const { countryCode, phone, otp, register, page = "WEB_IL_SITE" } = inp;

    const api = axios.create({
      baseURL: ApiUrlConstant.gateWayUrl,
      headers: register
        ? {
            "Content-Type": "application/json",
            "X-Page": `REGISTER_VALIDATE_${page}`,
            "X-Platform": "web",
            "X-Product": "1",
          }
        : {
            "Content-Type": "application/json",
            "product-id": "1000",
            "tenant-id": "1",
            platform: "web",
          },
    });

    const body = {
      isd_code: countryCode,
      phone: phone.toString(),
      otp: otp.toString(),
      product_id: 1000,
      tenant_id: 1,
    };

    const response = await api.post(`/api/v1/user/agents/login-with-otp`, body);

    return response?.data ?? Promise.reject("Failed to validate OTP");
  } catch (error: any) {
    console.error("Error validating OTP:", error);

    if (error?.response?.data?.detail)
      throw new Error(error.response.data.detail);
    throw new Error(error?.message || String(error));
  }
}
